/* eslint-disable max-lines */
import {
  addDays, addMonths, endOfMonth, getMonth, getWeek, getYear, isAfter, isBefore, isToday, isWeekend,
  startOfMonth, startOfWeek, format, endOfWeek, eachDayOfInterval, isEqual, Locale, formatISO9075
} from 'date-fns';
import findKey from 'lodash-es/findKey';

import { isNullOrUndefined } from '@app/util/util';
import {
  CalendarDay, CalendarMonth, CalendarDayType
} from '@design-system/components/molecules/calendar/entities/calendar.types';
import {
  DateCalendarDefaultDates, DateCalendarDefaultField, DateCalendarDefaultOnlyOneDay,
  DateCalendarField, DateCalendarLocale, DateCalendarLocaleValue, DateCalendarSelected
} from '@design-system/components/organisms/date-calendar/entities/date-calendar.types';

export class DateCalendar {

  private _months: CalendarMonth[];
  private _selectedDates: DateCalendarSelected = { left: null, right: null };
  private _field: DateCalendarField = DateCalendarDefaultField;
  private _isOnlyOneDate = DateCalendarDefaultOnlyOneDay;
  private _minDate: Date = null;
  private _maxDate: Date = null;
  private _disabledDates: number[] = [];
  private _locale: DateCalendarLocale = null;
  private _defaultDates: DateCalendarDefaultDates = [null, null];
  private _allowSameDateSelection = true;

  private readonly MonthName = 'LLLL';
  private readonly WeekDayName = 'EEEEEE';
  private readonly MonthNameAnchor = 'MMMM';
  private readonly TimeZoneVerification = 'T';

  constructor() {
    this._months = this.init();
  }

  get months(): CalendarMonth[] {
    return this._months;
  }

  get selectedDates(): DateCalendarSelected {
    return this._selectedDates;
  }

  get field(): DateCalendarField {
    return this._field;
  }

  get anchor(): string {
    const fieldByDefault = findKey(this.selectedDates, (date) => !isNullOrUndefined(date));
    const fieldAnchor = this.isOnlyOneDate ? this.field : (fieldByDefault ?? null);
    return !isNullOrUndefined(this.selectedDates?.[fieldAnchor]) ?
      format(this.selectedDates?.[fieldAnchor]?.date, this.MonthNameAnchor) : null;
  }

  get minDate(): Date {
    return this._minDate;
  }

  get maxDate(): Date {
    return this._maxDate;
  }

  get disabledDates(): number[] {
    return this._disabledDates;
  }

  get locale(): DateCalendarLocale {
    return this._locale;
  }

  get isOnlyOneDate(): boolean {
    return this._isOnlyOneDate;
  }

  get defaultDates(): Date[] {
    return this._defaultDates;
  }

  get allowSameDateSelection(): boolean {
    return this._allowSameDateSelection;
  }

  private get fieldIsLeft(): boolean {
    return this.field === 'left';
  }

  private get fieldIsRight(): boolean {
    return this.field === 'right';
  }

  private get sameDateHasBeenSelected(): boolean {
    return !this.isOnlyOneDate && !isNullOrUndefined(this.selectedDates?.left) && !isNullOrUndefined(this.selectedDates?.right)
      && isEqual(this.selectedDates?.left?.date, this.selectedDates?.right?.date);
  }

  private get localeValue(): Locale {
    return DateCalendarLocaleValue?.[this.locale] ?? null;
  }

  public setMinDate(date: Date): void {
    this._minDate = date;
    this._minDate?.setHours?.(0, 0, 0, 0);
    this.reloadByMinOrMaxDate();
  }

  public setMaxDate(date: Date): void {
    this._maxDate = date;
    this._maxDate?.setHours?.(0, 0, 0, 0);
    this.reloadByMinOrMaxDate();
  }

  public setLocale(locale: DateCalendarLocale): void {
    this._locale = locale;
    this._months = this.init();
  }

  public setField(field: DateCalendarField): void {
    this._field = field;
  }

  public setOnlyOneDate(value: boolean): void {
    this._isOnlyOneDate = value;
  }

  public setSelectedDates(dates: DateCalendarSelected): void {
    this._selectedDates = dates;
  }

  public setSelectedDatesField(day: CalendarDay, field: DateCalendarField): void {
    this._selectedDates[field] = day;
  }

  public setDefaultDates(dates: DateCalendarDefaultDates): void {
    this._defaultDates = dates;
  }

  public setAllowSameDateSelection(allowSameDateSelection: boolean): void {
    this._allowSameDateSelection = allowSameDateSelection;
  }

  public selectDefaultDates(defaultDates: DateCalendarDefaultDates, emitDates: Function): void {
    let dates: DateCalendarSelected = { left: null, right: null };

    const defaultMaxDates = defaultDates ? [...defaultDates].map(_date => {
      _date?.setHours?.(0, 0, 0, 0);
      return _date;
    }) as DateCalendarDefaultDates : [null, null] as DateCalendarDefaultDates;

    let mapDates = defaultMaxDates;

    if (
      (!this.allowSameDateSelection && isEqual(mapDates?.[0], mapDates?.[1])) ||
      (this.isOnlyOneDate && this.fieldIsLeft)
    ) {
      mapDates = [defaultMaxDates?.[0], null];
    }

    if (this.isOnlyOneDate && this.fieldIsRight) {
      mapDates = [null, defaultMaxDates?.[1]];
    }

    mapDates.forEach((defaultDay, defaultIndex) => {
      if (isNullOrUndefined(defaultDay)) {
        dates = { left: dates?.left ?? null, right: dates?.right ?? null };
      } else {
        this.months.some(month => {
          return month.grid.some(row => {
            const day = row.find(item => isEqual(item.date, defaultDay) && !item.disabled && item.visible);
            if (!isNullOrUndefined(day)) {
              if (this.isOnlyOneDate) {
                dates = this.setDefaultDatesByOnlyOneField(day);
              } else {
                dates = this.setDefaultDatesByField(dates, day, defaultIndex);
              }
              return true;
            } else {
              return false;
            }
          })
        });
      }
    });

    this.setDefaultDates(defaultMaxDates);
    this.setSelectedDates(dates);
    this.markDaysAsSelected();
    emitDates();
  }

  public setDisabledDates(dates: Date[]): void {
    dates.forEach(date => date.setHours?.(0, 0, 0, 0));
    this._disabledDates = dates.map(date => date.getTime());
    this.reloadByMinOrMaxDate();
  }

  public selectDay(day: CalendarDay, emitDates: Function): void {
    if (this.allowSameDateSelection || (!this.allowSameDateSelection &&
      !Object.values(this.selectedDates)?.find(selected => isEqual(selected?.date, day?.date))
    )) {
      if (this.isOnlyOneDate) {
        this.setSelectedDates({ left: this.fieldIsLeft ? day : null, right: this.fieldIsLeft ? null : day });
      } else {
        if (this.fieldIsLeft) {
          if (isAfter(day?.date, this.selectedDates?.right?.date)) {
            this.setSelectedDates({ left: day, right: null });
          } else {
            this.setSelectedDatesField(day, 'left');
          }
          this.setField('right');
        } else if (this.fieldIsRight) {
          if (isBefore(day?.date, this.selectedDates?.left?.date)) {
            this.setSelectedDates({ left: day, right: null });
            this.setField('right');
          } else {
            this.setSelectedDatesField(day, 'right');
            this.setField('left');
          }
        }
      }
      this.markDaysAsSelected();
      emitDates();
    }
  }

  private init(): CalendarMonth[] {
    const today = new Date();
    return Array.from({ length: 10 }).map((item, monthIndex) => {
      const monthRef = addMonths(today, monthIndex);
      const month = getMonth(monthRef);
      const year = getYear(monthRef);
      const grid = this.getMonthGrid(year, month);
      return {
        year: year,
        anchor: format(monthRef, this.MonthNameAnchor),
        isFromNextYear: getYear(today) !== year,
        name: format(new Date(monthRef), this.MonthName, { locale: this.localeValue }),
        weekdays: this.getWeekDays(),
        grid: grid.filter(row => !row.every(day => day.type === CalendarDayType.NextMonth))
      };
    });
  }

  private getMonthGrid(year: number, month: number): CalendarDay[][] {
    const rows = 6;
    const cols = 7;
    const startDate = startOfWeek(new Date(year, month, 1), { weekStartsOn: 0 });
    const dayStartOfMonth = startOfMonth(new Date(year, month));
    const dayEndOfMonth = endOfMonth(new Date(year, month));
    return Array.from({ length: rows * cols })
      .map((_, index) => this.getCalendarDay(startDate, index, dayStartOfMonth, dayEndOfMonth))
      .reduce((matrix, current, index, days) => (index % cols === 0) ?
        [...matrix, days.slice(index, index + cols)] : matrix, []
      );
  }

  private getCalendarDay(startDate: Date, index: number, dayStartOfMonth: Date, dayEndOfMonth: Date): CalendarDay {
    const day = addDays(startDate, index);

    day.setHours(0, 0, 0, 0);

    const type = isBefore(day, dayStartOfMonth) ? CalendarDayType.PreviousMonth :
      isAfter(day, dayEndOfMonth) ? CalendarDayType.NextMonth : CalendarDayType.CurrentMonth;
    return {
      value: day.getDate(),
      date: day,
      week: getWeek(day),
      type: type,
      isToday: isToday(day),
      isWeekend: isWeekend(day),
      identifier: format(day, 'yyyy-MM-dd'),
      iso: formatISO9075(day).replace(' ', this.TimeZoneVerification),
      formatted: this.getFormatted(day),
      disabled: this.isDisabledDay(day),
      visible: type === CalendarDayType.CurrentMonth,
      selected: undefined,
      dayInRange: undefined,
      dayInRangeRight: undefined,
      dayInRangeLeft: undefined,
    };
  }

  private getFormatted(day: Date): string {
    return `${format(day, 'EEE', { locale: this.localeValue })}, ${format(day, 'dd')} ${format(day, 'MMM', {
      locale: this.localeValue
    })}`;
  }

  private getWeekDays(): string[] {
    const now = new Date();
    const weekDays: string[] = [];
    const start = startOfWeek(now, { locale: this.localeValue, weekStartsOn: 0 });
    const end = endOfWeek(now, { locale: this.localeValue, weekStartsOn: 0 });
    eachDayOfInterval({ start, end }).forEach(day => {
      weekDays.push(format(day, this.WeekDayName, { locale: this.localeValue }));
    });
    return weekDays;
  }

  private isDisabledDay(day: Date): boolean {
    return isBefore(day, new Date()) && !isToday(day) || (
      !isNullOrUndefined(this.minDate) && isBefore(day, this.minDate)
    ) || (!isNullOrUndefined(this.maxDate) && isAfter(day, this.maxDate)) || this._disabledDates.includes(day.getTime());
  }

  private reloadByMinOrMaxDate(): void {
    this._months = this.months.map(month => ({
      ...month, grid: month.grid.map(row => row.map(day => {
        return {
          ...day,
          disabled: this.isDisabledDay(day.date)
        }
      }))
    }))
  }

  private markDaysAsSelected(): void {
    this._months = this.months.map(month => ({
      ...month, grid: month.grid.map(row => row.map(day => {
        if (!day.disabled && day.visible) {
          const isSelected = Object.values(this.selectedDates)?.some(rangeDay => isEqual(day.date, rangeDay?.date));
          const disabledRanges = this.isOnlyOneDate || this.sameDateHasBeenSelected;
          const formattedDay = {
            ...day,
            selected: isSelected,
            dayInRange: disabledRanges ? false :
              isAfter(day.date, this.selectedDates?.left?.date) && isBefore(day.date, this.selectedDates?.right?.date),
            dayInRangeRight: disabledRanges ? false : isEqual(day.date, this.selectedDates?.left?.date),
            dayInRangeLeft: disabledRanges ? false : isEqual(day.date, this.selectedDates?.right?.date),
          };
          this.refreshSelectedDates(isSelected, formattedDay);
          return formattedDay;
        } else {
          return day;
        }
      }))
    }));
  }

  private setDefaultDatesByOnlyOneField(day: CalendarDay): DateCalendarSelected {
    if (this.fieldIsLeft) {
      return { left: day, right: null };
    } else {
      return { left: null, right: day };
    }
  }

  private setDefaultDatesByField(dates: DateCalendarSelected, day: CalendarDay, defaultIndex: number): DateCalendarSelected {
    if (defaultIndex === 0) {
      return { left: day, right: dates?.right ?? null };
    } else {
      return { left: dates?.left ?? null, right: day };
    }
  }

  private refreshSelectedDates(isSelected: boolean, formattedDay: CalendarDay): void {
    if (isSelected) {
      if (this.sameDateHasBeenSelected) {
        this.setSelectedDates({ left: formattedDay, right: formattedDay });
      } else if (this.selectedDates?.left?.date && isEqual(this.selectedDates?.left?.date, formattedDay.date)) {
        this.setSelectedDatesField(formattedDay, 'left');
      } else if (this.selectedDates?.right?.date && isEqual(this.selectedDates?.right?.date, formattedDay.date)) {
        this.setSelectedDatesField(formattedDay, 'right');
      }
    }
  }
}
